<template>
  <AppLayout>
    <div class="flex flex-col max-w-2xl mx-auto w-full min-h-full text-surface-0 -m-4 p-4">
      <ErrorMessage
        v-if="loadApiKeysError"
        message="Error loading API keys"
        @tryAgain="loadApiKeys"
      />

      <div v-else-if="loadApiKeysPending" class="flex justify-center items-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl"></i>
        <span class="ml-2">Loading API keys...</span>
      </div>

      <div v-else class="mt-4">
        <div class="mb-6 flex justify-end">
          <Button
            variant="outlined"
            icon="pi pi-plus"
            label="Add API key"
            @click="addApiKeyDialogVisible = true"
          />
        </div>
        <div class="bg-surface-800 rounded-lg">
          <div v-if="apiKeys?.length === 0" class="p-8 text-center text-surface-400">
            No API keys found
          </div>
          <div v-else>
            <div
              v-for="(apiKey, index) in apiKeys ?? []"
              :key="apiKey.id"
              class="flex items-center justify-between p-4 hover:bg-surface-700 transition-colors"
              :class="{ 'border-b border-surface-700': index < (apiKeys?.length ?? 0) - 1 }"
            >
              <div class="flex-1">
                <div class="flex items-center gap-4">
                  <div class="flex-1">
                    <div class="font-medium text-surface-0">{{ apiKey.name }}</div>
                  </div>
                  <div class="flex flex-row gap-2 text-surface-400">
                    {{ formatDate(apiKey.created) }}
                  </div>
                </div>
              </div>
              <div class="ml-4">
                <Button
                  @click="remove(apiKey)"
                  severity="danger"
                  variant="text"
                  size="small"
                  rounded
                  icon="pi pi-times"
                  :loading="removePending"
                  class="!p-2"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="mt-8 pt-6 text-center">
          <a
            href="/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary-400 hover:text-primary-300 hover:underline inline-flex items-center gap-1"
          >
            View API Documentation
          </a>
        </div>
      </div>
    </div>
    <AddApiKeyDialog v-model:visible="addApiKeyDialogVisible" @created="loadApiKeys" />
  </AppLayout>
</template>

<script setup lang="ts">
import type { ApiKeyDto } from '@/api/generated';
import AppLayout from '@/components/AppLayout.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import { useApiClient } from '@/api/apiClient';
import { usePromise } from '@/composables/usePromise';
import { onMounted, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import Button from 'primevue/button';
import AddApiKeyDialog from './AddApiKeyDialog.vue';
import dayjs from 'dayjs';

const { api } = useApiClient();

const addApiKeyDialogVisible = ref(false);

const {
  run: loadApiKeys,
  data: apiKeys,
  pending: loadApiKeysPending,
  error: loadApiKeysError,
} = usePromise(async () => {
  const { data, error } = await api.apiKeyControllerList();
  if (error) {
    throw new Error('Failed to load API keys');
  }
  return data ?? [];
});

watch(loadApiKeysError, () => {
  if (loadApiKeysError.value) {
    console.error(loadApiKeysError.value);
    Sentry.captureException(loadApiKeysError.value);
  }
});

const {
  run: remove,
  pending: removePending,
  error: removeError,
} = usePromise(async (apiKey: ApiKeyDto) => {
  if (!apiKey.id) {
    return;
  }

  const { error } = await api.apiKeyControllerRemove({ path: { id: apiKey.id } });
  if (error) {
    throw new Error('Failed to remove API key');
  }

  await loadApiKeys();
});

watch(removeError, () => {
  if (removeError.value) {
    console.error(removeError.value);
    Sentry.captureException(removeError.value);
  }
});

function formatDate(date: number | undefined) {
  if (!date) {
    return '';
  }
  return dayjs(date).format('YYYY-MM-DD');
}

onMounted(() => {
  loadApiKeys();
});
</script>
